import { z } from 'zod';
import { JEWEL_SOCKET_MODES, JEWEL_VISUAL_SHAPES } from '../inventory/jewels';
import { decimalStringSchema } from './primitives';

/**
 * O que o cliente sabe sobre as joias de uma conta (gate V28C).
 *
 * ## Porque é um contrato próprio e não um campo no de skins
 *
 * O contrato de skins está na versão 4 e é lido por um cliente que já existe.
 * Acrescentar-lhe um campo obrigaria a subir a versão por causa de uma
 * funcionalidade que ainda nem sequer é adquirível - e obrigaria toda a
 * superfície das skins a mover-se sempre que a das joias mexesse. São duas
 * perguntas, e ficam em duas respostas.
 *
 * O vestiário junta as duas por `skinInstanceId`, que é a identidade que ambos
 * já usam.
 *
 * ## Identidade, e nunca posição
 *
 * Um assento é `(skinInstanceId, socketIndex)`, persistido. Uma joia é
 * `jewelInstanceId`. Nada aqui aceita "a terceira da lista" ou "o pip que o
 * jogador clicou": a interface pode reordenar o que quiser, e o assento 3
 * continua a ser o assento 3.
 *
 * ## O que este contrato não pode carregar
 *
 * Efeito, magnitude, chance, preço, curva ou promessa. As catorze joias de
 * socket não concedem nada nesta fase, e o Coração Primordial - a soberana - não
 * concede nada por POSSE: é incorporado numa nave, e o que atravessa este
 * contrato é a CONTAGEM incorporada, nunca o bónus que dela virá a ser derivado.
 * Um campo de efeito aqui seria um campo que alguém preencheria a partir do
 * cliente.
 *
 * ## A contagem viaja como string decimal
 *
 * `primordialHeartCount` é `numeric(40, 0)` no banco e `bigint` no servidor. Um
 * `number` de JSON perderia exactidão acima de 2^53, exactamente como perderia
 * num saldo - a mesma regra das moedas, e pela mesma razão.
 */

export const JEWELS_ROUTE = '/api/jewels' as const;
export const JEWEL_SOCKET_INSERT_ROUTE = '/api/jewels/socket/insert' as const;
export const JEWEL_SOCKET_REMOVE_ROUTE = '/api/jewels/socket/remove' as const;
export const JEWEL_SOCKET_SWAP_ROUTE = '/api/jewels/socket/swap' as const;
export const JEWEL_PRIMORDIAL_INCORPORATE_ROUTE = '/api/jewels/primordial/incorporate' as const;

export const JEWELS_CONTRACT_VERSION = 1;

/** Onde uma joia está encaixada, quando está. */
export const jewelSeatSchema = z
  .object({
    skinInstanceId: z.uuid(),
    socketIndex: z.number().int().min(0),
  })
  .strict();
export type JewelSeat = z.infer<typeof jewelSeatSchema>;

export const ownedJewelSchema = z
  .object({
    /** A identidade DESTA cópia. A única que um cliente pode nomear. */
    instanceId: z.uuid(),
    definitionId: z.string().min(1),
    /** O nome que o jogador lê. Nunca um id. */
    displayName: z.string().min(1),
    /**
     * O nível desta cópia.
     *
     * Um nesta fundação, sempre: não existe upgrade e o catálogo diz que uma
     * joia não tem tecto de nível. Viaja no fio porque a carta o mostra, e
     * porque o dia em que houver níveis não deve ser o dia em que o contrato
     * muda de forma.
     */
    level: z.number().int().min(1),
    socketMode: z.enum(JEWEL_SOCKET_MODES),
    visualShape: z.enum(JEWEL_VISUAL_SHAPES),
    /** A arte servida, do manifesto gerado. */
    assetFile: z.string().min(1),
    /** A máscara da borda. Só a silhueta tem; uma esfera usa um aro comum. */
    maskFile: z.string().min(1).nullable(),
    /** Onde está encaixada, ou `null` quando está livre. */
    seat: jewelSeatSchema.nullable(),
  })
  .strict();
export type OwnedJewel = z.infer<typeof ownedJewelSchema>;

/**
 * Os assentos de UMA cópia de skin.
 *
 * `sockets` é `null` quando a capacidade nunca foi sorteada - a cópia é
 * anterior à roleta. **Não é zero**, e a interface tem de o dizer por palavras:
 * ninguém sabe quantos assentos essa cópia teria, e desenhar nenhum seria
 * afirmar uma propriedade que o item não tem.
 */
export const skinSocketsSchema = z
  .object({
    skinInstanceId: z.uuid(),
    capacity: z.number().int().min(1).max(6).nullable(),
    sockets: z
      .array(
        z
          .object({
            index: z.number().int().min(0),
            jewelInstanceId: z.uuid().nullable(),
          })
          .strict(),
      )
      .nullable(),
  })
  .strict();
export type SkinSockets = z.infer<typeof skinSocketsSchema>;

/**
 * Uma nave da conta e os Corações Primordiais incorporados nela (gate V28F.A).
 *
 * ## Porque é que isto vive no contrato das joias
 *
 * Porque a pergunta é sobre corações, e a nave é apenas onde eles vivem. O
 * inventário mostra a contagem na carta de cada nave e o mercado mostra-a no
 * anúncio; esta lista é o DETALHE AUTENTICADO de que a montagem futura do perfil
 * de combate precisa - qual é a nave equipada, e quantos corações ela tem.
 *
 * ## Todas as naves entram, incluindo as de contagem zero
 *
 * Zero é uma resposta, e é diferente de a nave não aparecer. Uma nave sem
 * corações não tem linha em `ship_instances`, e o servidor traduz essa ausência
 * para `'0'` em vez de a esconder: a interface tem de poder dizer "esta nave não
 * tem nenhum" sem ter de inferir nada da ausência de uma entrada.
 *
 * ## O que NÃO está aqui
 *
 * Nenhum bónus, nenhum `damageBps`, nenhuma percentagem. A contagem é o facto; o
 * efeito é derivado dela por quem simula, e neste gate ninguém o deriva.
 */
export const shipPrimordialSchema = z
  .object({
    /** A identidade DESTA cópia de nave. A única que um cliente pode nomear. */
    shipInstanceId: z.uuid(),
    /** Quantos corações estão incorporados. String decimal, nunca um número. */
    primordialHeartCount: decimalStringSchema,
    /** Verdadeiro para a nave que está no slot agora. Exatamente uma o é. */
    equipped: z.boolean(),
  })
  .strict();
export type ShipPrimordial = z.infer<typeof shipPrimordialSchema>;

export const jewelsResponseSchema = z
  .object({
    contractVersion: z.literal(JEWELS_CONTRACT_VERSION),
    /** Todas as cópias que a conta tem, livres e encaixadas. */
    owned: z.array(ownedJewelSchema),
    /** Uma entrada por cópia de skin da conta, mesmo sem joia nenhuma. */
    skins: z.array(skinSocketsSchema),
    /**
     * Uma entrada por NAVE da conta, com a sua contagem de corações.
     *
     * ADITIVO, e por isso `contractVersion` não se move: `.default([])` faz com
     * que a resposta de um servidor anterior a este campo parseie para uma lista
     * vazia em vez de falhar, e um cliente anterior ignora a chave que não
     * conhece. Não há incompatibilidade a esconder.
     */
    ships: z.array(shipPrimordialSchema).default([]),
  })
  .strict();
export type JewelsResponse = z.infer<typeof jewelsResponseSchema>;

/**
 * Os três pedidos de mutação.
 *
 * `.strict()`, e por isso um cliente não pode enviar um efeito, um nível, uma
 * capacidade ou um preço - não há campo onde coubessem. A chave de idempotência
 * é do cliente porque é ele que repete; tudo o resto é decidido pelo servidor.
 */
const seatRequest = {
  skinInstanceId: z.uuid(),
  socketIndex: z.number().int().min(0),
  idempotencyKey: z.string().min(8).max(128),
};

export const jewelSocketInsertRequestSchema = z
  .object({ ...seatRequest, jewelInstanceId: z.uuid() })
  .strict();
export type JewelSocketInsertRequest = z.infer<typeof jewelSocketInsertRequestSchema>;

export const jewelSocketRemoveRequestSchema = z.object({ ...seatRequest }).strict();
export type JewelSocketRemoveRequest = z.infer<typeof jewelSocketRemoveRequestSchema>;

export const jewelSocketSwapRequestSchema = z
  .object({ ...seatRequest, jewelInstanceId: z.uuid() })
  .strict();
export type JewelSocketSwapRequest = z.infer<typeof jewelSocketSwapRequestSchema>;

/**
 * Incorporar um Coração Primordial numa nave (gate V28F.A).
 *
 * Três campos, e nenhum deles é um número, uma contagem, um efeito ou uma nave
 * de outra pessoa. `.strict()`: não há campo onde um cliente pudesse enviar
 * "quantos" corações incorporar, e é de propósito - a operação incorpora
 * exactamente UM, o que ele nomeou, e a contagem é decidida pelo servidor a
 * partir do que a nave já tinha.
 *
 * A operação é IRREVERSÍVEL: o coração é gasto e não volta ao inventário. Não há
 * rota inversa, e não é um esquecimento — desincorporar seria uma decisão de
 * produto que ninguém tomou, e inventá-la aqui seria inventar economia.
 */
export const jewelPrimordialIncorporateRequestSchema = z
  .object({
    /** A nave que fica com o coração. Tem de ser da conta que pede. */
    shipInstanceId: z.uuid(),
    /** O Coração Primordial a gastar. Uma cópia, uma incorporação. */
    jewelInstanceId: z.uuid(),
    idempotencyKey: z.string().min(8).max(128),
  })
  .strict();
export type JewelPrimordialIncorporateRequest = z.infer<
  typeof jewelPrimordialIncorporateRequestSchema
>;

/**
 * A resposta de uma mutação carrega a VIEW INTEIRA.
 *
 * Não um delta, e não uma confirmação. O cliente não aplica nada de forma
 * otimista: envia a intenção e desenha o que o servidor devolver. Um delta
 * obrigaria as duas pontas a concordar sobre como aplicá-lo, e a divergirem no
 * primeiro caso que ninguém previu.
 */
export const jewelMutationResponseSchema = z
  .object({
    contractVersion: z.literal(JEWELS_CONTRACT_VERSION),
    /** Verdadeiro quando o pedido já tinha sido feito com esta mesma chave. */
    replayed: z.boolean(),
    owned: z.array(ownedJewelSchema),
    skins: z.array(skinSocketsSchema),
    /** As naves e as suas contagens, pelo mesmo princípio: a view inteira. */
    ships: z.array(shipPrimordialSchema).default([]),
  })
  .strict();
export type JewelMutationResponse = z.infer<typeof jewelMutationResponseSchema>;
